/* eslint-disable no-param-reassign */
import { useCallback, useMemo } from 'react'

import { isEqualDate, normalizeDate } from 'utils/convertFromLocalDate'

import type { Center } from 'api-client'
import type { OrderPlanStock } from 'components/parts/OrderPlanTransportLot/type'
import type { StockExtended } from 'hooks/useStock'

/** API の ship_quantity_plan は OpenAPI 上オブジェクト型だが実体は数値のことが多い */
export function coerceShipPlanNumber(plan: unknown): number | null {
  if (plan == null) return null
  if (typeof plan === 'number' && !Number.isNaN(plan)) return plan
  return null
}

/** rec_date と systemDate が揃うとき、本日セル用の素出荷を解決するためのコンテキスト */
export type StockCenter2ShipRawContext = {
  recDate?: Date | string | null
  systemDate: Date
  /** true: 本日は plan → scheduled、false: 本日は plan のみ */
  todayShipRecDelayFlg: boolean
}

/**
 * 出荷の素数量（expected / ship_quantity は使わない）
 * - context なし: plan → scheduled（従来互換）
 * - rec_date=本日 かつ today_ship_rec_delay_flg=false: ship_quantity_plan のみ
 * - 上記以外（本日で delay true、または本日より後）: plan → scheduled
 */
export function getStockCenter2ShipRawValue(
  options: {
    shipQuantityPlan?: number | null | unknown
    shipQuantityScheduled?: number | null
  },
  context?: StockCenter2ShipRawContext | null
): number {
  const planNum = coerceShipPlanNumber(options.shipQuantityPlan)
  const sched = options.shipQuantityScheduled
  const schedNum =
    sched != null && sched !== undefined && !Number.isNaN(Number(sched)) ? Number(sched) : null

  if (!context) {
    if (planNum != null) return planNum
    if (schedNum != null) return schedNum
    return 0
  }

  const rec = normalizeDate(context.recDate)
  const sys = normalizeDate(context.systemDate)
  const isToday = rec != null && sys != null && isEqualDate(rec, sys)

  if (isToday && !context.todayShipRecDelayFlg) {
    return planNum ?? 0
  }

  if (planNum != null) return planNum
  if (schedNum != null) return schedNum
  return 0
}

/**
 * 入荷の実効数量（グリッド表示・在庫計算で共通）
 * - enable_update_arrival=false: arrivalQuantity
 * - enable_update_arrival=true: arrival_quantity_plan → orderQuantity → orderQuantityRec → orderQuantityRecommend（値がある先頭）
 */
export function getStockCenter2EffectiveArrivalQuantity(item: {
  enableUpdateArrival?: boolean | null
  arrivalQuantity?: number | null
  orderQuantity?: number | null
  arrivalQuantityPlan?: unknown
  orderQuantityRec?: number | null
  orderQuantityRecommend?: number | null
}): number {
  if (item.enableUpdateArrival !== true) {
    return Number(item.arrivalQuantity ?? 0)
  }
  const planNum = coerceShipPlanNumber(item.arrivalQuantityPlan)
  if (planNum != null) {
    return planNum
  }
  const oq = item.orderQuantity
  if (oq != null && oq !== undefined && !Number.isNaN(Number(oq))) {
    return Number(oq)
  }
  const oqr = item.orderQuantityRec
  if (oqr != null && oqr !== undefined && !Number.isNaN(Number(oqr))) {
    return Number(oqr)
  }
  const rec = item.orderQuantityRecommend
  if (rec != null && rec !== undefined && !Number.isNaN(Number(rec))) {
    return Number(rec)
  }
  return Number(item.arrivalQuantity ?? 0)
}

export interface StockQuantityCompareOptions {
  /**
   * Day list used by the grid (used to restrict comparison to visible range)
   */
  daylist?: Array<{ datevalue?: Date | string | null; dateno?: number | null }> | null
  startDateTypeCode?: number | string | null
  endDateTypeCode?: number | string | null

  /**
   * Output container for mismatched lines (unique).
   * Values are `centerId|orgSupplierId|itemCode` when center/supplier exist on the row; otherwise `itemCode` only (legacy).
   */
  mismatchedItemCodes: Set<string>

  /**
   * Optional: tolerance for numeric comparison
   */
  tolerance?: number

  /**
   * Optional: console log mismatch details (debug)
   */
  logDetails?: boolean
}

export interface HandleStockCalculationOptions {
  compareStockQuantityWithAPI?: StockQuantityCompareOptions
  /**
   * 指定時は各行の centerId / companyId でセンターマスタから calcStockShippingorderFlg を解決する（複数センター対応）
   */
  centerSearch?: Center
}

/**
 * Interface for calculation parameters
 */
export interface CalcStockParams {
  systemDate: Date
  calcStockShippingorderFlg: boolean
  /** mst_center.today_ship_rec_delay_flg — 本日セルで scheduled まで見るか */
  todayShipRecDelayFlg: boolean
}

/**
 * Interface for stock item
 */
export interface StockItemBase {
  shipQuantity?: number | null
  salesorderQuantity?: number | null
  recDate?: Date | string | null
  enableShip?: boolean | null
}

/**
 * Interface for stock item in list calculation
 */
export interface StockItemForCalculation extends StockItemBase {
  centerId?: number | string | null
  itemCode?: string | null
  arrivalQuantity?: number | null
  stockQuantity?: number | null
  stockDays?: number | null
  /** 在庫調整数 — 在庫数から減算 */
  stockQuantityShipStop?: number | null
  dateType?: string | null
  rectype?: string | null
  renewalNumber?: number | null
}

/**
 * Options for list calculation
 */
export interface CalcStockListOptions {
  startFromToday?: boolean
  groupByKey?: (item: StockItemForCalculation) => string
  itemFilter?: (item: StockItemForCalculation) => boolean
  handleRenewal?: boolean
  useMorningStock?: boolean
  storeInitialStockDays?: boolean
  customStockCalc?: (
    prevStock: number,
    arrival: number,
    shipStockBase: number,
    item: StockItemForCalculation,
    index: number
  ) => number
}
/**
 * useCalcStock hook
 */
export const useCalcStock = () => {
  /**
   * ship_quantity_stock_base（素出荷は getStockCenter2ShipRawValue の rec_date / todayShipRecDelayFlg ルールに従う）
   * - rec_date = 本日: salesorder_quantity + 素出荷（delay_flg=true: plan→scheduled、false: planのみ）
   * - rec_date > 本日 かつ calc_stock_shippingorder_flg: salesorder + 素出荷（plan→scheduled）
   * - rec_date > 本日 かつ !calc_stock_shippingorder_flg: 素出荷のみ
   */
  const calculateShipQuantityStockBase = useCallback((
    stock: StockItemBase,
    params: CalcStockParams
  ): number => {

    const useShippingOrderFlg = params.calcStockShippingorderFlg
    
    const recDate = stock.recDate ? new Date(stock.recDate) : null
    if (recDate) {
      recDate.setHours(0, 0, 0, 0)
    }

    let result = Number(stock.shipQuantity)

    if (isEqualDate(recDate, params.systemDate) || ((normalizeDate(recDate)! > normalizeDate(params.systemDate)!) && useShippingOrderFlg)) {
      const salesorderQuantity = stock.salesorderQuantity ? Number(stock.salesorderQuantity) : 0
      result += salesorderQuantity
    }
    
    return result
  }, [])

  /**
   * 翌日以降、7営業日分のship_quantity_stock_baseの平均
   * - Average of ship_quantity_stock_base for next 7 business days (enableShip = true)
   * - If < 7 business days available, returns 0
   */
  const calculateAvgShipQuantityStockBase = useCallback((
    futureStocks: StockItemBase[],
    params: CalcStockParams
  ): number => {
    // Filter only business days (enableShip = true)
    const businessDays = futureStocks
      .filter(stock => stock.enableShip === true)
      .slice(0, 7)

    // Return 0 if fewer than 7 business days available
    if (businessDays.length < 7) {
      return 0
    }

    // Calculate ship_quantity_stock_base for each day
    const shipStockBases = businessDays.map(stock =>
      Number(calculateShipQuantityStockBase(stock, params))
    )

    // Calculate average
    const total = shipStockBases.reduce((sum, value) => sum + Number(value), 0)
    return Number(total / 7)
  }, [calculateShipQuantityStockBase])

  /**
   * 在庫数の計算式
   * stock_quantity = previous_stock + arrival_quantity - ship_quantity_stock_base - stock_quantity_ship_stop
   */
  const calculateStockQuantity = useCallback((
    previousStock: number,
    arrivalQuantity: number,
    shipQuantityStockBase: number,
    stockQuantityShipStop?: number
  ): number => {
    const prev = Number(previousStock)
    const arrival = Number(arrivalQuantity)
    const shipBase = Number(shipQuantityStockBase)
    const shipStop = Number(stockQuantityShipStop ?? 0)

    return Number(prev + arrival - shipBase - shipStop)
  }, [])

  /**
   * 在庫日数の計算式
   * stock_days = stock_quantity / avg_ship_quantity_stock_base
   */
  const calculateStockDays = useCallback((
    stockQuantity: number,
    avgShipQuantityStockBase: number
  ): number | null => {
    const stockQty = Number(stockQuantity)
    const avgShip = Number(avgShipQuantityStockBase)

    return Number(stockQty / avgShip)
  }, 
  [])

  /**
   * Helper for grid-based calculations
   * Returns individual calculation functions that can be used in grid cell updates
   */
  const getGridCalculators = useCallback(() => ({
    calcCellStock: (
      beforeStock: number,
      arrival: number,
      ship: number,
      salesOrder: number,
      recDate: Date | string | null | undefined,
      params: CalcStockParams,
      stockQuantityShipStop = 0
    ): number => {
      const shipStockBase = calculateShipQuantityStockBase(
        {
          shipQuantity: ship,
          salesorderQuantity: salesOrder,
          recDate,
        },
        params
      )
      
      return calculateStockQuantity(
        Math.max(Number(beforeStock), 0),
        Number(arrival),
        Number(shipStockBase),
        Number(stockQuantityShipStop)
      )
    },

    /**
     * Calculate single cell stock days value
     * @param stockValue Current stock quantity
     * @param futureShips Array of future ship data (next 7+ business days)
     * @param params Calculation parameters
     * @returns Calculated stock days or null if insufficient data or division by zero
     */
    calcCellStockDays: (
      stockValue: number,
      futureShips: Array<{
        shipQuantity: number
        salesorderQuantity: number
        enableShip: boolean
        recDate?: Date | string | null
      }>,
      params: CalcStockParams
    ): number | null => {
      const businessDays = futureShips.filter(s => s.enableShip).slice(0, 7)

      // Return null if fewer than 7 business days available
      if (businessDays.length < 7) {
        return null
      }

      const avgShipStockBase = calculateAvgShipQuantityStockBase(
        businessDays,
        params
      )

      // Use calculateStockDays which handles division by zero
      // Returns null when avgShipStockBase is 0
      return calculateStockDays(Number(stockValue), Number(avgShipStockBase))
    },

    // Original functions for compatibility
    calculateShipQuantityStockBase,
    calculateAvgShipQuantityStockBase,
    calculateStockQuantity,
    calculateStockDays,
  }), [
    calculateShipQuantityStockBase,
    calculateAvgShipQuantityStockBase,
    calculateStockQuantity,
    calculateStockDays,
  ])

  return {
    calculateShipQuantityStockBase,
    calculateAvgShipQuantityStockBase,
    calculateStockQuantity,
    calculateStockDays,
    getGridCalculators,
  }
}

/**
 * centerSearch から該当センターの calcStockShippingorderFlg を解決して CalcStockParams を組み立てる（グリッド行・API1行単位用）
 */
export const buildCalcStockParamsForCenter = (
  systemDate: Date | string | null | undefined,
  centerId: number | string | null | undefined,
  companyId: number | null | undefined,
  centerSearch?: Center
): CalcStockParams => {
  const sysDate = systemDate ? new Date(systemDate) : new Date()
  sysDate.setHours(0, 0, 0, 0)

  let calcStockShippingorderFlg = false
  let todayShipRecDelayFlg = false
  if (centerSearch?.list?.length && centerId != null && companyId != null) {
    const centerItem = centerSearch.list.find(
      (item) => item.centerId === Number(centerId) && item.companyId === Number(companyId)
    )
    if (centerItem) {
      calcStockShippingorderFlg = centerItem.calcStockShippingorderFlg as boolean
      todayShipRecDelayFlg = centerItem.todayShipRecDelayFlg === true
    }
  }

  return {
    systemDate: sysDate,
    calcStockShippingorderFlg,
    todayShipRecDelayFlg,
  }
}

/**
 * @param systemDate - System date to use for calculations (defaults to current date)
 * @param centerId - Center ID to fetch the flag for
 * @param companyId - Company ID to fetch the flag for
 * @param centerSearch - Center data from API (required)
 * @returns CalcStockParams object with normalized dates and fetched flag
 */
export const useCalcStockParams = (
  systemDate?: Date | string | null,
  centerId?: number | string | null,
  companyId?: number | null,
  centerSearch?: Center
): CalcStockParams =>
  useMemo(
    () => buildCalcStockParamsForCenter(systemDate, centerId, companyId, centerSearch),
    [systemDate, centerId, companyId, centerSearch]
  )

/**
 * Calculate stock quantity and stock days for a list of stock items
 * @param stockDataResponse - Original stock data from API response
 * @param stockList - Updated stock list (may contain user edits)
 * @param loginUserInfo - User info containing systemDate
 * @param calcFunctions - Calculation functions from useCalcStock hook
 * @param calcParams - Calculation parameters (CalcStockParams)
 * @param isInitialCalculation - Whether this is the initial calculation (to store initialStockDays)
 * @returns Filtered stock list with calculated stockQuantity and stockDays
 * （在庫数 = 前日在庫 + 実効入荷 − ship_quantity_stock_base − stock_quantity_ship_stop）
 */
export const handleStockCalculation = (
  stockDataResponse: StockExtended[],
  stockList: StockExtended[],
  loginUserInfo: any,
  calcFunctions: {
    calculateShipQuantityStockBase: (stock: StockItemBase, params: CalcStockParams) => number
    calculateStockQuantity: (
      previousStock: number,
      arrivalQuantity: number,
      shipQuantityStockBase: number,
      stockQuantityShipStop?: number
    ) => number
    calculateAvgShipQuantityStockBase: (futureStocks: any[], params: any) => number
    calculateStockDays: (stockQuantity: number, avgShipQuantityStockBase: number) => number | null
  },
  calcParams: CalcStockParams,
  isInitialCalculation = false,
  pastStockOptimize = false,
  options?: HandleStockCalculationOptions,
): OrderPlanStock[] => {
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const companyFallback = loginUserInfo?.companyId ?? null

  const getRowCalcParams = (item: Pick<StockExtended, 'centerId' | 'companyId'>): CalcStockParams => {
    if (options?.centerSearch) {
      return buildCalcStockParamsForCenter(
        loginUserInfo?.systemDate ?? Date.now(),
        item.centerId ?? null,
        item.companyId ?? companyFallback,
        options.centerSearch
      )
    }
    return calcParams
  }

  const toDateKey = (d: Date | string | null | undefined): string | null => {
    const nd = normalizeDate(d)
    return nd ? nd.toLocaleDateString('sv-SE') : null
  }

  const makeKey = (itemCode?: string | null, recDate?: Date | string | null): string | null => {
    if (!itemCode) return null
    const dk = toDateKey(recDate)
    if (!dk) return null
    return `${itemCode}|${dk}`
  }

  /** Compare / API quantity map: disambiguates multi-center × multi-supplier rows */
  const makeApiQtyKey = (s: Pick<StockExtended, 'itemCode' | 'recDate' | 'centerId' | 'orgSupplierId'>): string | null => {
    if (!s.itemCode) return null
    const dk = toDateKey(s.recDate ?? null)
    if (!dk) return null
    const cid = s.centerId ?? ''
    const oid = s.orgSupplierId ?? ''
    return `${cid}|${oid}|${s.itemCode}|${dk}`
  }

  const makeMismatchScopeKey = (s: Pick<StockExtended, 'itemCode' | 'centerId' | 'orgSupplierId'>): string | null => {
    if (!s.itemCode) return null
    const cid = s.centerId ?? ''
    const oid = s.orgSupplierId ?? ''
    if (cid !== '' || oid !== '') {
      return `${cid}|${oid}|${s.itemCode}`
    }
    return s.itemCode
  }

  const sameMergeRow = (a: StockExtended, b: StockExtended): boolean =>
    a.itemCode === b.itemCode &&
    isEqualDate(a.recDate, b.recDate) &&
    (a.centerId ?? null) === (b.centerId ?? null) &&
    (a.orgSupplierId ?? '') === (b.orgSupplierId ?? '')

  // Merge API response list with current edited list (O(n) with a map)
  const stockListMap = new Map<string, StockExtended>()
  stockList.forEach((s) => {
    const key = makeKey(s.itemCode ?? null, s.recDate ?? null)
    if (key) stockListMap.set(key, s)
  })

  const stockListData = (stockDataResponse.map((stockRes) => {
    const stockFromList = stockList.find((stock) => sameMergeRow(stock, stockRes))

    if (stockFromList) {
      return {
        ...stockFromList
      };
    }

    return stockRes;
  }) ?? []) as OrderPlanStock[]

  // Optional: compare calculated stockQuantity vs API stockQuantity while calculating (avoid extra pass)
  const compareOpt = options?.compareStockQuantityWithAPI
  const tolerance = compareOpt?.tolerance ?? 0.001
  let apiStockQtyMap: Map<string, number> | null = null
  let displayDates: Set<string> | null = null

  if (compareOpt) {
    apiStockQtyMap = new Map<string, number>()
    stockDataResponse.forEach((s) => {
      if (!s.itemCode || !s.recDate) return
      if (s.stockQuantity === undefined || s.stockQuantity === null) return
      const key = makeApiQtyKey(s)
      if (key) apiStockQtyMap!.set(key, Number(s.stockQuantity ?? 0))
    })

    displayDates = new Set<string>()
    const { daylist, startDateTypeCode, endDateTypeCode } = compareOpt
    if (daylist && daylist.length > 0) {
      // Calculate date range filters once
      const startIndex = startDateTypeCode ? Math.abs(Number(startDateTypeCode)) : 0
      const endIndex = endDateTypeCode ? Number(endDateTypeCode) : 0
      const totalVisibleDays = startIndex + endIndex
      const hasStartFilter = startDateTypeCode !== undefined && startDateTypeCode !== null && Number(startDateTypeCode) !== 0
      const hasEndFilter = endDateTypeCode !== undefined && endDateTypeCode !== null && Number(endDateTypeCode) !== 0
      const shouldFilterByDateRange = hasStartFilter || hasEndFilter

      daylist.forEach((day) => {
        // Filter by date range if needed
        if (shouldFilterByDateRange) {
          const dayDateno = day.dateno || 0
          if (dayDateno === 0 || dayDateno > totalVisibleDays) {
            return
          }
        }

        if (day.datevalue) {
          const dk = toDateKey(day.datevalue)
          if (dk) displayDates!.add(dk)
        }
      })
    }
  }

  // API may return dateType or rectype for date type
  const getDateType = (item: StockItemForCalculation) => item.dateType ?? item.rectype
  const stockTodays = stockListData.filter(
    (item) =>
      item.recDate && getDateType(item) === 'today' && item.stockQuantity !== undefined && item.stockQuantity !== null
  )

  stockTodays.forEach((stockToday) => {
    const stockListbyToday = stockListData
      .filter(
        (stock) =>
          stock.recDate &&
          stock.itemCode === stockToday.itemCode &&
          (stock.centerId ?? null) === (stockToday.centerId ?? null) &&
          (stock.orgSupplierId ?? '') === (stockToday.orgSupplierId ?? '') &&
          stock.stockQuantity !== undefined &&
          stock.stockQuantity !== null &&
          normalizeDate(stock.recDate)! >= normalizeDate(stockToday.recDate!)!
      )
      .sort((a, b) => normalizeDate(a.recDate!)!.getTime() - normalizeDate(b.recDate!)!.getTime())

    stockListbyToday.forEach((itemStock, index) => {
      const arrivalQuantity = getStockCenter2EffectiveArrivalQuantity(itemStock)
      const rowCalcParams = getRowCalcParams(itemStock)

      const shipQtyRaw = getStockCenter2ShipRawValue(
        {
          shipQuantityPlan: itemStock.shipQuantityPlan,
          shipQuantityScheduled: itemStock.shipQuantityScheduled,
        },
        {
          recDate: itemStock.recDate,
          systemDate: rowCalcParams.systemDate,
          todayShipRecDelayFlg: rowCalcParams.todayShipRecDelayFlg,
        }
      )

      // Calculate ship_quantity_stock_base for current item（本日は delay_flg で plan のみ可）
      const shipQuantityStockBase = calcFunctions.calculateShipQuantityStockBase(
        {
          shipQuantity: shipQtyRaw,
          salesorderQuantity: itemStock.salesorderQuantity,
          recDate: itemStock.recDate,
          enableShip: itemStock.enableShip,
        },
        rowCalcParams
      )
      // Assign calculated ship_quantity_stock_base and ship_quantity_optimize_base back to item
      itemStock.shipQuantityStockBase = shipQuantityStockBase
      itemStock.shipQuantityOptimizeBase = shipQuantityStockBase

      // Use calculateStockQuantity with ship_quantity_stock_base (not shipQuantity)
      // Match backend: prev is capped at 0 (max(0, stock)), but result can be negative
      const shipStopQty = Number(itemStock.stockQuantityShipStop ?? 0)

      if (getDateType(itemStock) === 'today') {
        const stockQuantityRecMorning = itemStock.stockQuantityRecMorning && itemStock.stockQuantityRecMorning > 0 ? itemStock.stockQuantityRecMorning : 0
        const calculatedStock = calcFunctions.calculateStockQuantity(
          stockQuantityRecMorning,
          arrivalQuantity,
          shipQuantityStockBase,
          shipStopQty
        )
        itemStock.stockQuantity = calculatedStock
      } else {
        const prevStockQuantity = stockListbyToday[index - 1]?.stockQuantity ?? 0
        const prevCapped = Math.max(0, prevStockQuantity)
        const calculatedStock = calcFunctions.calculateStockQuantity(
          prevCapped,
          arrivalQuantity,
          shipQuantityStockBase,
          shipStopQty
        )
        itemStock.stockQuantity = calculatedStock
      }

      // stock_quantity_rec_morning[d] = stock_quantity[d-1] (today 以降のみ)
      const nextItem = stockListbyToday[index + 1]
      if (nextItem) {
        nextItem.stockQuantityRecMorning = itemStock.stockQuantity
      }

      // Compare calculated stockQuantity with API stockQuantity (only for visible range if provided)
      if (compareOpt && apiStockQtyMap && itemStock.itemCode) {
        const key = makeApiQtyKey(itemStock)
        if (key) {
          // Check if date is in display range (if displayDates is empty, compare all)
          const dateKey = toDateKey(itemStock.recDate ?? null)
          const isInDisplayRange = !displayDates || displayDates.size === 0 || (dateKey !== null && displayDates.has(dateKey))
          
          if (isInDisplayRange) {
            const apiQty = apiStockQtyMap.get(key)
            if (apiQty !== undefined) {
              const calculatedQty = Number(itemStock.stockQuantity ?? 0)
              const isMismatch = Math.abs(calculatedQty - apiQty) > tolerance

              if (isMismatch) {
                const scopeKey = makeMismatchScopeKey(itemStock)
                if (scopeKey) compareOpt.mismatchedItemCodes.add(scopeKey)
                // if (compareOpt.logDetails) {
                //   const { recDate } = itemStock
                //   const recDateStr =
                //     recDate != null
                //       ? normalizeDate(recDate)?.toISOString?.() ?? String(recDate)
                //       : ''
                //   // eslint-disable-next-line no-console
                //   console.warn('[useCalcStock2] stock_quantity mismatch (API vs client calc)', {
                //     scopeKey,
                //     itemCode: itemStock.itemCode,
                //     orgItemCode: itemStock.orgItemCode,
                //     centerId: itemStock.centerId,
                //     orgSupplierId: itemStock.orgSupplierId,
                //     recDate: recDateStr,
                //     dateType: getDateType(itemStock),
                //     apiStockQuantity: apiQty,
                //     calculatedStockQuantity: calculatedQty,
                //     diff: calculatedQty - apiQty,
                //     tolerance,
                //     effectiveArrival: arrivalQuantity,
                //     shipQuantityStockBase: itemStock.shipQuantityStockBase,
                //     enableUpdateArrival: itemStock.enableUpdateArrival,
                //     todayShipRecDelayFlg: rowCalcParams.todayShipRecDelayFlg,
                //     calcStockShippingorderFlg: rowCalcParams.calcStockShippingorderFlg,
                //     apiKey: key,
                //   })
                // }
              }
            }
          }
        }
      }

      // Get future shipments for stock days calculation
      const futureShipments = stockListbyToday.slice(index + 1)
        .filter(item => item.enableShip === true && item.shipQuantity != null)
        .slice(0, 7);

      if (futureShipments.length < 7) {
        itemStock.stockDays = null;
        itemStock.avgShipQuantityStockBase = null;
        if (isInitialCalculation) itemStock.initialStockDays = 0;
      } else {
        const futureShipmentsForAvg = futureShipments.map((item) => {
          const p = getRowCalcParams(item)
          return {
            ...item,
            shipQuantity: getStockCenter2ShipRawValue(
              {
                shipQuantityPlan: item.shipQuantityPlan,
                shipQuantityScheduled: item.shipQuantityScheduled,
              },
              {
                recDate: item.recDate,
                systemDate: p.systemDate,
                todayShipRecDelayFlg: p.todayShipRecDelayFlg,
              }
            ),
          }
        })
        const avgShipQuantityStockBase = calcFunctions.calculateAvgShipQuantityStockBase(
          futureShipmentsForAvg,
          rowCalcParams
        );
        const stockDays = calcFunctions.calculateStockDays(
          itemStock.stockQuantity ?? 0,
          avgShipQuantityStockBase
        );
        // Assign calculated avg_ship_quantity_stock_base back to item
        itemStock.avgShipExpected = avgShipQuantityStockBase;
        itemStock.avgShipQuantityStockBase = avgShipQuantityStockBase;
        itemStock.stockDays = stockDays;
        if (isInitialCalculation) itemStock.initialStockDays = stockDays ?? 0;
      }
      if (!pastStockOptimize) {
        itemStock.arrivalQuantityPlan = arrivalQuantity
      }
    })
  })
  if (pastStockOptimize) {
    return stockListData
  }

  return stockListData
    .filter((rec) => {
      const orderDate = normalizeDate(rec.orderDate)
      return (
        orderDate &&
        orderDate >= systemDate! &&
        rec.arrivalQuantity !== undefined &&
        rec.arrivalQuantity !== null &&
        rec.enableArrival === true
      )
  })
}

/**
 * Compare calculated stock quantity with API stock quantity
 * Only compares items within the display range (based on daylist + startDateTypeCode/endDateTypeCode)
 * @param stockDataResponse - Original stock data from API response (contains stockQuantity from API)
 * @param calculatedStockList - Calculated stock list from handleStockCalculation
 * @param daylist - List of days to display (optional, if provided only compares items in this range)
 * @param startDateTypeCode - Start date type code for filtering visible range (optional)
 * @param endDateTypeCode - End date type code for filtering visible range (optional)
 * @returns Array of itemCodes where calculated stockQuantity differs from API stockQuantity
 */
export const compareStockQuantityWithAPI = (
  stockDataResponse: StockExtended[],
  calculatedStockList: OrderPlanStock[],
  daylist?: Array<{ datevalue?: Date | string | null; dateno?: number | null }> | null,
  startDateTypeCode?: number | string | null,
  endDateTypeCode?: number | string | null
): string[] => {
  const mismatchedItemCodes: string[] = []
  const uniqueItemCodes = new Set<string>()

  // Calculate visible date range based on startDateTypeCode and endDateTypeCode
  const startIndex = startDateTypeCode ? Math.abs(Number(startDateTypeCode)) : 0
  const endIndex = endDateTypeCode ? Number(endDateTypeCode) : 14
  const totalVisibleDays = startIndex + endIndex

  // Create a set of display dates if daylist is provided
  // Only include dates that are within the visible range (based on dateno)
  const displayDates = new Set<string>()
  if (daylist && daylist.length > 0) {
    daylist.forEach((day) => {
      // Filter by dateno if startDateTypeCode/endDateTypeCode are provided
      const hasStartFilter = startDateTypeCode !== undefined && startDateTypeCode !== null && Number(startDateTypeCode) !== 0
      const hasEndFilter = endDateTypeCode !== undefined && endDateTypeCode !== null && Number(endDateTypeCode) !== 0
      const shouldFilterByDateRange = hasStartFilter || hasEndFilter
      
      if (shouldFilterByDateRange) {
        const dayDateno = day.dateno || 0
        // Only include days within visible range (dateno > 0 and dateno <= totalVisibleDays)
        // This matches the logic in DataList/StockGrid genDayCols: shouldHide = dayDateno === 0 || dayDateno > totalVisibleDays
        if (dayDateno === 0 || dayDateno > totalVisibleDays) {
          return // Skip days outside visible range
        }
      }
      
      if (day.datevalue) {
        const normalizedDate = normalizeDate(day.datevalue)
        if (normalizedDate) {
          displayDates.add(normalizedDate.toLocaleDateString('sv-SE'))
        }
      }
    })
  }

  calculatedStockList.forEach((calculatedStock) => {
    if (!calculatedStock.itemCode || !calculatedStock.recDate) return

    // If daylist is provided and we have filtered display dates, only compare items within the display range
    // If displayDates is empty (no filter applied), compare all items in daylist
    if (daylist && daylist.length > 0) {
      // If we have filtered display dates, check if item is in range
      if (displayDates.size > 0) {
        const stockDateStr = normalizeDate(calculatedStock.recDate)?.toLocaleDateString('sv-SE')
        const isInDisplayRange = stockDateStr && displayDates.has(stockDateStr)
        if (!isInDisplayRange) {
          return // Skip items not in display range (but stockQuantity was still calculated for all items)
        }
      }
      // If displayDates is empty (no filter), we compare all items (fall through)
    }

    // Find corresponding item in API response
    const apiStock = stockDataResponse.find(
      (stock) =>
        stock.itemCode === calculatedStock.itemCode &&
        isEqualDate(stock.recDate, calculatedStock.recDate) &&
        stock.stockQuantity !== undefined &&
        stock.stockQuantity !== null
    )

    if (apiStock) {
      const calculatedQty = calculatedStock.stockQuantity ?? 0
      const apiQty = apiStock.stockQuantity ?? 0

      // Compare with tolerance for floating point differences
      if (Math.abs(calculatedQty - apiQty) > 0.001) {
        if (!uniqueItemCodes.has(calculatedStock.itemCode)) {
          uniqueItemCodes.add(calculatedStock.itemCode)
          mismatchedItemCodes.push(calculatedStock.itemCode)
        }
      }
    }
  })

  return mismatchedItemCodes
}
